import type { ClassMemberGradeLayout } from './classMembersStorage';

/**
 * 멤버 "고정 번호" 표시 규칙 (모든 화면 공통)
 *
 * - 그룹 안에 studentNumber가 하나도 없으면(예전 데이터) → 목록 순서대로 1, 2, 3 …
 * - 하나라도 있으면 → 번호가 있는 멤버는 그 번호 그대로(중간 빈 번호 허용),
 *   번호가 없는 멤버는 (가장 큰 번호 + 1)부터 목록 순서대로 이어 붙인다.
 */
export const MEMBER_STUDENT_NUMBER_MIN = 1;
export const MEMBER_STUDENT_NUMBER_MAX = 9999;

type NumberedMember = { id: string; studentNumber?: number | null };

export function normalizeMemberStudentNumber(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n)) return null;
  const t = Math.trunc(n);
  if (t < MEMBER_STUDENT_NUMBER_MIN || t > MEMBER_STUDENT_NUMBER_MAX) return null;
  return t;
}

/** 그룹 멤버 중 고정 번호가 하나라도 있는지 */
export function groupHasFixedMemberNumbers(rows: readonly NumberedMember[]): boolean {
  return rows.some((r) => normalizeMemberStudentNumber(r.studentNumber) != null);
}

/** 그룹(한 목록) 기준 표시 번호 배열 — rows와 같은 인덱스 */
export function getClassMemberDisplayNumbers(rows: readonly NumberedMember[]): number[] {
  if (!groupHasFixedMemberNumbers(rows)) return rows.map((_, i) => i + 1);
  let max = 0;
  for (const r of rows) {
    const n = normalizeMemberStudentNumber(r.studentNumber);
    if (n != null && n > max) max = n;
  }
  let next = max;
  return rows.map((r) => {
    const n = normalizeMemberStudentNumber(r.studentNumber);
    if (n != null) return n;
    next += 1;
    return next;
  });
}

/** 그룹(한 목록) 기준 id → 표시 번호 */
export function buildClassMemberNumberMap(rows: readonly NumberedMember[]): Map<string, number> {
  const nums = getClassMemberDisplayNumbers(rows);
  const map = new Map<string, number>();
  rows.forEach((r, i) => map.set(r.id, nums[i]!));
  return map;
}

/** 학년 레이아웃 전체: 그룹별 표시 번호를 한 Map으로 */
export function buildLayoutMemberNumberMap(
  layout: Pick<ClassMemberGradeLayout, 'membersByGroupId'> | null | undefined
): Map<string, number> {
  const map = new Map<string, number>();
  if (!layout) return map;
  for (const list of Object.values(layout.membersByGroupId ?? {})) {
    buildClassMemberNumberMap(list ?? []).forEach((n, id) => {
      if (!map.has(id)) map.set(id, n);
    });
  }
  return map;
}

/** 레이아웃 어디든 고정 번호가 있는지 */
export function layoutHasFixedMemberNumbers(
  layout: Pick<ClassMemberGradeLayout, 'membersByGroupId'> | null | undefined
): boolean {
  if (!layout) return false;
  return Object.values(layout.membersByGroupId ?? {}).some((list) => groupHasFixedMemberNumbers(list ?? []));
}

/** 표시 번호 기준 안정 정렬(같은 번호면 기존 순서 유지) */
export function sortMembersByDisplayNumber<T extends NumberedMember>(rows: readonly T[]): T[] {
  const nums = getClassMemberDisplayNumbers(rows);
  return rows
    .map((row, i) => ({ row, i, n: nums[i]! }))
    .sort((a, b) => a.n - b.n || a.i - b.i)
    .map((x) => x.row);
}

/** 모든 멤버에 현재 표시 번호를 studentNumber로 확정(이미 같으면 같은 객체 유지) */
export function materializeMemberNumbers<T extends NumberedMember>(rows: readonly T[]): T[] {
  const nums = getClassMemberDisplayNumbers(rows);
  return rows.map((row, i) =>
    normalizeMemberStudentNumber(row.studentNumber) === nums[i] ? row : { ...row, studentNumber: nums[i] }
  );
}

/**
 * 드래그로 순서 변경. 고정 번호 그룹이면 "지금 쓰는 번호 묶음"(오름차순)을 새 순서대로 다시 배정한다.
 * 예) 1~16, 18 → 옮긴 뒤에도 위에서부터 1, 2, … 16, 18 (빈 번호 17은 그대로 비어 있음).
 * 번호가 없는 예전 그룹은 순서만 바꾼다.
 */
export function reorderMembersKeepingNumbers<T extends NumberedMember>(
  rows: readonly T[],
  fromIndex: number,
  toIndex: number
): T[] {
  const n = rows.length;
  if (fromIndex < 0 || fromIndex >= n || toIndex < 0 || toIndex >= n) return rows.slice();
  const moved = rows.slice();
  const [item] = moved.splice(fromIndex, 1);
  moved.splice(toIndex, 0, item!);
  if (!groupHasFixedMemberNumbers(rows)) return moved;
  const numbers = getClassMemberDisplayNumbers(rows)
    .slice()
    .sort((a, b) => a - b);
  return moved.map((row, i) =>
    normalizeMemberStudentNumber(row.studentNumber) === numbers[i] ? row : { ...row, studentNumber: numbers[i] }
  );
}

/** 표시 번호가 겹치는 번호 집합 */
export function findDuplicateMemberNumbers(rows: readonly NumberedMember[]): Set<number> {
  const seen = new Set<number>();
  const dup = new Set<number>();
  for (const n of getClassMemberDisplayNumbers(rows)) {
    if (seen.has(n)) dup.add(n);
    else seen.add(n);
  }
  return dup;
}

/** 새 멤버에 줄 번호(고정 번호 그룹이면 최대+1, 아니면 null = 순서 번호) */
export function nextMemberStudentNumber(rows: readonly NumberedMember[]): number | null {
  if (!groupHasFixedMemberNumbers(rows)) return null;
  const nums = getClassMemberDisplayNumbers(rows);
  const max = nums.reduce((m, n) => (n > m ? n : m), 0);
  return Math.min(MEMBER_STUDENT_NUMBER_MAX, max + 1);
}

/** 표시용 "07번" 형태 */
export function formatMemberNumberLabel(n: number): string {
  return `${String(n).padStart(2, '0')}번`;
}
